import React, { useState, useEffect } from 'react';
import WeatherTab from './WeatherTab';

const ManageProfileAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };
  const authHeadersForForm = {
    'Authorization': `Bearer ${adminToken}`
  };

  // Sub-tab state
  const [manageProfileSubTab, setManageProfileSubTab] = useState('avatars');

  // Avatar states
  const [avatars, setAvatars] = useState([]);
  const [editingAvatar, setEditingAvatar] = useState(null);
  const [newAvatar, setNewAvatar] = useState({ name: '', image_url: '', gender: 'female' });
  const [showAvatarModal, setShowAvatarModal] = useState(false);

  // Avatar Gender states
  const [avatarGenders, setAvatarGenders] = useState([]);
  const [editingGender, setEditingGender] = useState(null);
  const [newGender, setNewGender] = useState({ name: '', icon: '', color: '#6b7280' });
  const [showGenderModal, setShowGenderModal] = useState(false);

  // Feature Flags states
  const [featureFlags, setFeatureFlags] = useState({
    learning_goals_enabled: true,
    learning_goals_premium: false,
    notification_settings_enabled: true,
    notification_settings_premium: false,
    notif_email_visible: true,
    notif_learning_reminders_visible: true,
    notif_achievement_visible: true,
    notif_subscription_visible: true,
    notif_holiday_visible: true
  });
  const [loadingFeatureFlags, setLoadingFeatureFlags] = useState(false);

  // App Settings states
  const [appSettings, setAppSettings] = useState({});

  // ── Data Loading ──

  const loadAvatarsData = async () => {
    try {
      const avatarsRes = await fetch(`${API}/admin/avatars`, { headers: authHeaders });
      if (avatarsRes.ok) {
        const avatarsData = await avatarsRes.json();
        setAvatars(Array.isArray(avatarsData) ? avatarsData : []);
      } else {
        setAvatars([]);
      }
      const gendersRes = await fetch(`${API}/admin/avatar-genders`, { headers: authHeaders });
      if (gendersRes.ok) {
        const gendersData = await gendersRes.json();
        setAvatarGenders(Array.isArray(gendersData) ? gendersData : []);
      } else {
        setAvatarGenders([]);
      }
    } catch (error) {
      console.error("Error loading avatars:", error);
    }
  };

  const loadFeatureFlags = async () => {
    try {
      const response = await fetch(`${API}/admin/feature-flags`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setFeatureFlags(prev => ({
          ...prev,
          ...data,
          notif_email_visible: data.notif_email_visible !== undefined ? data.notif_email_visible : (prev.notif_email_visible !== undefined ? prev.notif_email_visible : true),
          notif_learning_reminders_visible: data.notif_learning_reminders_visible !== undefined ? data.notif_learning_reminders_visible : (prev.notif_learning_reminders_visible !== undefined ? prev.notif_learning_reminders_visible : true),
          notif_achievement_visible: data.notif_achievement_visible !== undefined ? data.notif_achievement_visible : (prev.notif_achievement_visible !== undefined ? prev.notif_achievement_visible : true),
          notif_subscription_visible: data.notif_subscription_visible !== undefined ? data.notif_subscription_visible : (prev.notif_subscription_visible !== undefined ? prev.notif_subscription_visible : true),
          notif_holiday_visible: data.notif_holiday_visible !== undefined ? data.notif_holiday_visible : (prev.notif_holiday_visible !== undefined ? prev.notif_holiday_visible : true)
        }));
      }
    } catch (error) {
      console.error('Error loading feature flags:', error);
    }
  };

  const loadAppSettings = async () => {
    try {
      const response = await fetch(`${API}/admin/settings`, { headers: authHeaders });
      if (response.ok) {
        const data = await response.json();
        setAppSettings(data);
      }
    } catch (error) {
      console.error('Error loading app settings:', error);
    }
  };

  // Load data when sub-tabs change
  useEffect(() => {
    if (manageProfileSubTab === 'avatars') {
      loadAvatarsData();
    } else if (manageProfileSubTab === 'feature-controls') {
      loadFeatureFlags();
      loadAppSettings();
    }
  }, [manageProfileSubTab]);

  // Initial load
  useEffect(() => {
    loadAvatarsData();
  }, []);

  // ── Feature Flag Functions ──

  const updateFeatureFlag = async (flagName, value) => {
    setLoadingFeatureFlags(true);
    setFeatureFlags(prev => ({ ...prev, [flagName]: value }));
    try {
      const formData = new FormData();
      const formValue = typeof value === 'boolean' ? (value ? 'true' : 'false') : value;
      formData.append(flagName, formValue);

      const response = await fetch(`${API}/admin/feature-flags`, {
        method: 'PUT',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Feature setting updated!', 'success');
        loadFeatureFlags();
      } else {
        showNotification('Failed to update feature setting', 'error');
        if (typeof value === 'boolean') {
          setFeatureFlags(prev => ({ ...prev, [flagName]: !value }));
        }
      }
    } catch (error) {
      console.error('Error updating feature flag:', error);
      showNotification('Failed to update feature setting', 'error');
      if (typeof value === 'boolean') {
        setFeatureFlags(prev => ({ ...prev, [flagName]: !value }));
      }
    } finally {
      setLoadingFeatureFlags(false);
    }
  };

  // ── Avatar Management Functions ──

  const handleCreateAvatar = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', newAvatar.name);
      formData.append('icon', '👤');
      formData.append('gender', newAvatar.gender || 'female');
      if (newAvatar.image_url) {
        formData.append('image_url', newAvatar.image_url);
      }

      const response = await fetch(`${API}/admin/avatars`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Avatar created successfully', 'success');
        setNewAvatar({ name: '', image_url: '', gender: 'female' });
        setShowAvatarModal(false);
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to create avatar';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {
          errorMsg = `Failed to create avatar (${response.status})`;
        }
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error creating avatar:', error);
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Unknown error');
      showNotification('Failed to create avatar: ' + errorMsg, 'error');
    }
  };

  const handleUpdateAvatar = async (avatarId, updates) => {
    try {
      const formData = new FormData();
      if (updates.name !== undefined) formData.append('name', updates.name);
      if (updates.icon !== undefined) formData.append('icon', updates.icon);
      if (updates.image_url !== undefined) formData.append('image_url', updates.image_url);
      if (updates.gender !== undefined) formData.append('gender', updates.gender);
      if (updates.is_active !== undefined) formData.append('is_active', updates.is_active);

      const response = await fetch(`${API}/admin/avatars/${avatarId}`, {
        method: 'PUT',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Avatar updated successfully', 'success');
        setEditingAvatar(null);
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to update avatar';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {
          errorMsg = `Failed to update avatar (${response.status})`;
        }
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      const errorMsg = typeof error === 'string' ? error : (error?.message || 'Unknown error');
      showNotification('Failed to update avatar: ' + errorMsg, 'error');
    }
  };

  const handleSetDefaultAvatar = async (avatarId) => {
    try {
      const response = await fetch(`${API}/admin/avatars/${avatarId}/set-default`, {
        method: 'POST',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Default avatar set successfully', 'success');
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to set default avatar';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {}
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error setting default avatar:', error);
      showNotification('Failed to set default avatar', 'error');
    }
  };

  const handleDeleteAvatar = async (avatarId) => {
    if (!window.confirm('Are you sure you want to delete this avatar? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/avatars/${avatarId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Avatar deleted successfully', 'success');
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to delete avatar';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {}
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error deleting avatar:', error);
      showNotification('Failed to delete avatar', 'error');
    }
  };

  const handleToggleAvatarActive = async (avatarId, currentStatus) => {
    await handleUpdateAvatar(avatarId, { is_active: !currentStatus });
  };

  // ── Avatar Gender Management Functions ──

  const handleCreateGender = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('name', newGender.name);
      formData.append('icon', newGender.icon);
      formData.append('color', newGender.color);

      const response = await fetch(`${API}/admin/avatar-genders`, {
        method: 'POST',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Gender category created successfully', 'success');
        setShowGenderModal(false);
        setNewGender({ name: '', icon: '', color: '#6b7280' });
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to create gender category';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {}
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error creating gender:', error);
      showNotification('Failed to create gender category', 'error');
    }
  };

  const handleUpdateGender = async (genderId, updates) => {
    try {
      const formData = new FormData();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      const response = await fetch(`${API}/admin/avatar-genders/${genderId}`, {
        method: 'PUT',
        headers: authHeadersForForm,
        body: formData
      });

      if (response.ok) {
        showNotification('Gender category updated successfully', 'success');
        setEditingGender(null);
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to update gender category';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {}
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error updating gender:', error);
      showNotification('Failed to update gender category', 'error');
    }
  };

  const handleDeleteGender = async (genderId) => {
    if (!window.confirm('Are you sure you want to delete this gender category?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/avatar-genders/${genderId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Gender category deleted successfully', 'success');
        loadAvatarsData();
      } else {
        let errorMsg = 'Failed to delete gender category';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) {}
        showNotification(errorMsg, 'error');
      }
    } catch (error) {
      console.error('Error deleting gender:', error);
      showNotification('Failed to delete gender category', 'error');
    }
  };

  const handleToggleGenderActive = async (genderId, currentStatus) => {
    await handleUpdateGender(genderId, { is_active: !currentStatus });
  };

  // ── Render ──

  return (
    <div className="space-y-6">
      <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
        👤 Manage Profile
      </h3>

      {/* Sub-tab navigation */}
      <div className="flex gap-2 flex-wrap">
        {[
          { id: 'avatars', label: 'Avatar & Gender', icon: '👤' },
          { id: 'feature-controls', label: 'Feature Controls', icon: '🎛️' },
          { id: 'weather', label: 'Weather', icon: '🌤️' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setManageProfileSubTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
              manageProfileSubTab === tab.id
                ? 'bg-blue-600 text-white'
                : isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {manageProfileSubTab === 'avatars' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              👤 Avatar Management
            </h3>
            <button
              onClick={() => setShowAvatarModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              ➕ Add New Avatar
            </button>
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
              💡 <strong>Tip:</strong> Upload avatar images in Image Manager → Avatars tab, then assign them here via Dropbox URL or Image Manager. Disabled avatars won't show to users. Set one as default for fallback.
            </p>
          </div>

          {/* Avatars Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {avatars.map(avatar => (
              <div
                key={avatar.id}
                className={`relative p-4 rounded-lg border-2 transition-all ${
                  avatar.is_default
                    ? isDarkMode
                      ? 'border-yellow-500 bg-yellow-900/20 ring-2 ring-yellow-400'
                      : 'border-yellow-500 bg-yellow-50 ring-2 ring-yellow-400'
                    : avatar.is_active
                      ? isDarkMode
                        ? 'border-green-500 bg-gray-700'
                        : 'border-green-500 bg-white'
                      : isDarkMode
                        ? 'border-gray-600 bg-gray-800 opacity-50'
                        : 'border-gray-300 bg-gray-100 opacity-50'
                }`}
              >
                {/* Default Badge */}
                {avatar.is_default && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-yellow-500 text-white text-xs font-bold rounded">
                    ⭐ DEFAULT
                  </div>
                )}

                {/* Status Badge */}
                <div className="absolute top-2 right-2">
                  <button
                    onClick={() => handleToggleAvatarActive(avatar.id, avatar.is_active)}
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      avatar.is_active
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-gray-500 text-white hover:bg-gray-600'
                    }`}
                    title={avatar.is_active ? 'Click to disable' : 'Click to enable'}
                  >
                    {avatar.is_active ? '✓' : '✗'}
                  </button>
                </div>

                {/* Gender Badge */}
                <div className="absolute top-2 left-2">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                    avatar.gender === 'female'
                      ? 'bg-pink-500 text-white'
                      : avatar.gender === 'male'
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-400 text-white'
                  }`}>
                    {avatar.gender === 'female' ? '♀' : avatar.gender === 'male' ? '♂' : '⚪'}
                  </span>
                </div>

                {/* Avatar Display */}
                <div className="flex flex-col items-center space-y-3 pt-6">
                  {avatar.image_url ? (
                    <img
                      src={avatar.image_url.startsWith('/') ? `${API.replace(/\/api$/, '')}${avatar.image_url}` : avatar.image_url}
                      alt={avatar.name}
                      className={`w-16 h-16 rounded-full object-cover object-top border-2 ${
                        isDarkMode ? 'border-gray-600' : 'border-gray-300'
                      } ${!avatar.is_active && 'grayscale'}`}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`w-16 h-16 rounded-full border-2 items-center justify-center text-2xl ${
                    isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-100'
                  } ${!avatar.is_active && 'grayscale'} ${avatar.image_url ? 'hidden' : 'flex'}`}>
                    👤
                  </div>

                  {editingAvatar?.id === avatar.id ? (
                    <div className="w-full space-y-2">
                      <input
                        type="text"
                        value={editingAvatar.name}
                        onChange={(e) => setEditingAvatar({...editingAvatar, name: e.target.value})}
                        className={`w-full px-2 py-1 text-sm rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Name"
                      />
                      <input
                        type="text"
                        value={editingAvatar.image_url || ''}
                        onChange={(e) => setEditingAvatar({...editingAvatar, image_url: e.target.value})}
                        className={`w-full px-2 py-1 text-sm rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="📦 Dropbox URL"
                      />
                      <select
                        value={editingAvatar.gender || 'unisex'}
                        onChange={(e) => setEditingAvatar({...editingAvatar, gender: e.target.value})}
                        className={`w-full px-2 py-1 text-sm rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                      >
                        {avatarGenders.length > 0 ? (
                          avatarGenders.map(gender => (
                            <option key={gender.id} value={gender.id}>
                              {gender.id === 'female' ? '♀' : gender.id === 'male' ? '♂' : '⚪'} {gender.name}
                            </option>
                          ))
                        ) : (
                          <>
                            <option value="female">♀ Female</option>
                            <option value="male">♂ Male</option>
                          </>
                        )}
                      </select>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateAvatar(avatar.id, {
                            name: editingAvatar.name,
                            image_url: editingAvatar.image_url,
                            gender: editingAvatar.gender
                          })}
                          className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingAvatar(null)}
                          className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`text-sm font-medium text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {avatar.name}
                      </p>
                      {avatar.image_url && (
                        <p className={`text-xs text-center truncate max-w-full ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          📦 Has image
                        </p>
                      )}
                      <div className="flex gap-1 w-full flex-wrap justify-center">
                        <button
                          onClick={() => setEditingAvatar({id: avatar.id, name: avatar.name, image_url: avatar.image_url, gender: avatar.gender})}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          ✏️
                        </button>
                        {!avatar.is_default && (
                          <button
                            onClick={() => handleSetDefaultAvatar(avatar.id)}
                            className="px-2 py-1 bg-yellow-500 text-white rounded text-xs hover:bg-yellow-600"
                            title="Set as default"
                          >
                            ⭐
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteAvatar(avatar.id)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          {avatars.length === 0 && (
            <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <p className="text-lg mb-2">No avatars found</p>
              <p className="text-sm">Click "Add New Avatar" to create your first avatar</p>
            </div>
          )}

          {/* Gender Categories Management */}
          <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-4">
              <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🏷️ Gender Categories
              </h4>
              <button
                onClick={() => setShowGenderModal(true)}
                className="px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
              >
                ➕ Add Gender
              </button>
            </div>

            {/* Note: Feature not currently active */}
            <div className={`p-3 rounded-lg mb-2 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-300'}`}>
              <p className={`text-sm font-medium ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>
                ⚠️ NOT CURRENTLY FUNCTIONAL - Reserved for future features
              </p>
              <p className={`text-xs mt-1 ${isDarkMode ? 'text-yellow-500/80' : 'text-yellow-600/80'}`}>
                This section is independent and not connected to frontend yet. Can be used for future avatar grouping features.
              </p>
            </div>

            <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-purple-900/20 border border-purple-700' : 'bg-purple-50 border border-purple-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                💡 Manage gender categories for avatars. Avatars in the profile will be grouped by these categories in order.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {avatarGenders.map(gender => (
                <div
                  key={gender.id}
                  className={`relative p-4 rounded-lg border-2 transition-all ${
                    gender.is_active
                      ? isDarkMode
                        ? 'border-gray-600 bg-gray-700'
                        : 'border-gray-300 bg-white'
                      : isDarkMode
                        ? 'border-gray-700 bg-gray-800 opacity-50'
                        : 'border-gray-200 bg-gray-100 opacity-50'
                  }`}
                  style={{ borderLeftColor: gender.color, borderLeftWidth: '4px' }}
                >
                  {/* Status Badge */}
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={() => handleToggleGenderActive(gender.id, gender.is_active)}
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        gender.is_active
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-gray-500 text-white hover:bg-gray-600'
                      }`}
                      title={gender.is_active ? 'Click to disable' : 'Click to enable'}
                    >
                      {gender.is_active ? '✓' : '✗'}
                    </button>
                  </div>

                  {editingGender?.id === gender.id ? (
                    <div className="space-y-2 pt-4">
                      <input
                        type="text"
                        value={editingGender.name}
                        onChange={(e) => setEditingGender({...editingGender, name: e.target.value})}
                        className={`w-full px-2 py-1 text-sm rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Name"
                      />
                      <input
                        type="text"
                        value={editingGender.icon}
                        onChange={(e) => setEditingGender({...editingGender, icon: e.target.value})}
                        className={`w-full px-2 py-1 text-sm rounded border ${
                          isDarkMode
                            ? 'bg-gray-600 border-gray-500 text-white'
                            : 'bg-white border-gray-300 text-gray-900'
                        }`}
                        placeholder="Icon/Emoji"
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={editingGender.color}
                          onChange={(e) => setEditingGender({...editingGender, color: e.target.value})}
                          className="w-10 h-8 rounded cursor-pointer"
                        />
                        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Color</span>
                      </div>
                      {/* Avatar URL Field */}
                      <div>
                        <label className={`text-xs block mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          🖼️ Avatar Image URL (Dropbox/Image link)
                        </label>
                        <input
                          type="text"
                          value={editingGender.avatar_url || ''}
                          onChange={(e) => setEditingGender({...editingGender, avatar_url: e.target.value})}
                          className={`w-full px-2 py-1 text-sm rounded border ${
                            isDarkMode
                              ? 'bg-gray-600 border-gray-500 text-white'
                              : 'bg-white border-gray-300 text-gray-900'
                          }`}
                          placeholder="https://www.dropbox.com/..."
                        />
                        {editingGender.avatar_url && (
                          <div className="mt-1 flex items-center gap-2">
                            <img
                              src={editingGender.avatar_url.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '')}
                              alt="Preview"
                              className="w-10 h-10 rounded-full object-cover border-2 border-gray-400"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <span className={`text-xs ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>✓ Preview</span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateGender(gender.id, {
                            name: editingGender.name,
                            icon: editingGender.icon,
                            color: editingGender.color,
                            avatar_url: editingGender.avatar_url
                          })}
                          className="flex-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingGender(null)}
                          className="flex-1 px-2 py-1 bg-gray-600 text-white rounded text-xs hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center space-y-2 pt-2">
                      {/* Show avatar image if set, otherwise show icon */}
                      {gender.avatar_url ? (
                        <img
                          src={gender.avatar_url.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('?dl=0', '')}
                          alt={gender.name}
                          className="w-12 h-12 rounded-full object-cover border-2"
                          style={{ borderColor: gender.color }}
                          onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                        />
                      ) : null}
                      <span className={`text-3xl ${gender.avatar_url ? 'hidden' : ''}`}>{gender.icon}</span>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {gender.name}
                      </p>
                      {gender.avatar_url && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-900/50 text-green-400' : 'bg-green-100 text-green-600'}`}>
                          🖼️ Custom Avatar
                        </span>
                      )}
                      <div className="flex items-center gap-1">
                        <div
                          className="w-4 h-4 rounded-full border"
                          style={{ backgroundColor: gender.color }}
                        ></div>
                        <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                          Order: {gender.order}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEditingGender({
                            id: gender.id,
                            name: gender.name,
                            icon: gender.icon,
                            color: gender.color,
                            avatar_url: gender.avatar_url || ''
                          })}
                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteGender(gender.id)}
                          className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {avatarGenders.length === 0 && (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <p className="text-sm">No gender categories found. Default categories will be used.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {manageProfileSubTab === 'feature-controls' && (
        <div className="space-y-6">
          <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
            🎛️ Feature Controls
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Enable/disable features and set premium status. Test features as free, then decide which ones to make Pro-only.
          </p>

          <div className="space-y-6">
            {/* Notification Settings */}
            <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'}`}>
              <h5 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🔔 Notification Settings
              </h5>
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  ℹ️ This section controls the "Notification Settings" that users see in their Profile page. You can enable/disable the entire section, make it Pro-only, or show/hide individual notification options.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Feature Enabled</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={featureFlags.notification_settings_enabled}
                      onChange={(e) => updateFeatureFlag('notification_settings_enabled', e.target.checked)}
                      disabled={loadingFeatureFlags}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pro Feature Only</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={featureFlags.notification_settings_premium}
                      onChange={(e) => updateFeatureFlag('notification_settings_premium', e.target.checked)}
                      disabled={loadingFeatureFlags || !featureFlags.notification_settings_enabled}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-600"></div>
                  </label>
                </div>
              </div>

              {/* Show / Hide Individual Notifications */}
              {featureFlags.notification_settings_enabled && (
                <div className="mt-4 pt-4 border-t border-gray-600">
                  <h6 className={`text-sm font-semibold mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Show / Hide Individual Notifications
                  </h6>
                  <div className="space-y-2">
                    {[
                      { key: 'notif_email_visible', label: '📧 Email Notifications' },
                      { key: 'notif_learning_reminders_visible', label: '⏰ Daily Learning Reminders' },
                      { key: 'notif_achievement_visible', label: '🏆 Achievement Unlocked Alerts' },
                      { key: 'notif_subscription_visible', label: '💳 Subscription Renewal Reminders' },
                      { key: 'notif_holiday_visible', label: '🎉 Moroccan Holiday Reminders' }
                    ].map(item => (
                      <div key={item.key} className="flex items-center justify-between">
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.label}</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={featureFlags[item.key] !== false}
                            onChange={(e) => {
                              updateFeatureFlag(item.key, e.target.checked);
                            }}
                            disabled={loadingFeatureFlags}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-600 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Email Security Message */}
            <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-white'}`}>
              <h5 className={`text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                🔒 Email Security Message
              </h5>
              <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-blue-900/30 border border-blue-700' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  ℹ️ This message is shown under the Email field in Profile → Edit Profile. Users cannot change their email for security.
                </p>
              </div>
            </div>
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20 border border-blue-700' : 'bg-blue-50 border border-blue-300'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              💡 <strong>Tip:</strong> Keep features free while testing. Once satisfied, toggle "Pro Feature Only" to monetize them.
            </p>
          </div>

          <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-green-900/20 border border-green-700' : 'bg-green-50 border border-green-300'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-green-300' : 'text-green-800'}`}>
              ℹ️ <strong>Note:</strong> All features are currently visible in the user's profile section. Users can configure them from Profile → Settings.
            </p>
          </div>
        </div>
      )}

      {manageProfileSubTab === 'weather' && (
        <div>
          <WeatherTab
            API={API}
            adminToken={adminToken}
            isDarkMode={isDarkMode}
            showNotification={showNotification}
          />
        </div>
      )}

      {/* Avatar Creation Modal */}
      {showAvatarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Add New Avatar
              </h3>
              <button
                onClick={() => {
                  setShowAvatarModal(false);
                  setNewAvatar({ name: '', image_url: '', gender: 'female' });
                }}
                className="text-2xl hover:opacity-70"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateAvatar} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Avatar Name
                </label>
                <input
                  type="text"
                  value={newAvatar.name}
                  onChange={(e) => setNewAvatar({...newAvatar, name: e.target.value})}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="e.g., Cool Robot"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  📦 Dropbox Image URL (optional)
                </label>
                <input
                  type="url"
                  value={newAvatar.image_url}
                  onChange={(e) => setNewAvatar({...newAvatar, image_url: e.target.value})}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="https://www.dropbox.com/..."
                />
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Paste a direct Dropbox link. Change ?dl=0 to ?raw=1 for direct image.
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  🏷️ Gender Category
                </label>
                <select
                  value={newAvatar.gender || 'female'}
                  onChange={(e) => setNewAvatar({...newAvatar, gender: e.target.value})}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  {avatarGenders.length > 0 ? (
                    avatarGenders.filter(g => g.is_active).map(gender => (
                      <option key={gender.id} value={gender.id}>
                        {gender.id === 'female' ? '♀' : gender.id === 'male' ? '♂' : '⚪'} {gender.name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="female">♀ Female</option>
                      <option value="male">♂ Male</option>
                    </>
                  )}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Create Avatar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAvatarModal(false);
                    setNewAvatar({ name: '', image_url: '', gender: 'female' });
                  }}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Gender Category Creation Modal */}
      {showGenderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-lg p-6 max-w-md w-full ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                Add Gender Category
              </h3>
              <button
                onClick={() => {
                  setShowGenderModal(false);
                  setNewGender({ name: '', icon: '', color: '#6b7280' });
                }}
                className="text-2xl hover:opacity-70"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateGender} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Category Name
                </label>
                <input
                  type="text"
                  value={newGender.name}
                  onChange={(e) => setNewGender({...newGender, name: e.target.value})}
                  className={`w-full px-4 py-2 border rounded-lg ${
                    isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                  placeholder="e.g., Non-Binary"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Icon/Emoji
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={newGender.icon}
                    onChange={(e) => setNewGender({...newGender, icon: e.target.value})}
                    className={`flex-1 px-3 py-2 border rounded-lg text-2xl text-center ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                    placeholder="🌈"
                    required
                  />
                  {newGender.icon && (
                    <div className={`w-12 h-12 flex items-center justify-center text-3xl border-2 rounded-lg ${
                      isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                    }`}>
                      {newGender.icon}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Display Color
                </label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={newGender.color}
                    onChange={(e) => setNewGender({...newGender, color: e.target.value})}
                    className="w-14 h-10 rounded cursor-pointer border-0"
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {newGender.color}
                  </span>
                </div>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  This color will be used to highlight this category in the profile avatar selection.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium"
                >
                  Create Category
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowGenderModal(false);
                    setNewGender({ name: '', icon: '', color: '#6b7280' });
                  }}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageProfileAdmin;
